import { FC, RefObject, useCallback } from 'react';

import ItemPageActions from '~/app/components/ItemPage/components/ItemPageActions';
import useUnpublishLivePage from '~/app/components/ItemPage/hooks/useUnpublishLivePage';
import { toShareUrl } from '~/app/components/ItemPage/utils';
import { useDispatch } from '~/app/lib/store/redux';
import { useAppRouter } from '~/app/lib/router2';
import { useI18n } from '~/app/lib/i18n';
import { ItemTypes } from '~/types';

import { useAppToast } from '~/app/components/NextApp/lib/CoreUi';
import { CustomPageItemContext } from '../types';
import useSelectArtist from '~/app/lib/hooks/useSelectArtist';
import deleteCustomPageAction from '~/app/lib/store/customPages/actions/delete';

export const CustomPageActions: FC<{
  itemContext: CustomPageItemContext;
  isOpen: boolean;
  actionButtonRef: RefObject<HTMLElement>;
  onClose: () => void;
}> = ({ itemContext, isOpen, actionButtonRef, onClose }) => {
  const unpublishPage = useUnpublishLivePage(ItemTypes.ALBUM);
  const customPage = itemContext.data.item;
  const artist = useSelectArtist(customPage.artistId);
  const router = useAppRouter();
  const dispatch = useDispatch();
  const showToast = useAppToast();
  const { t } = useI18n();

  const shareUrl = toShareUrl({
    isOwned: customPage.isOwned,
    isDraft: customPage.isDraft,
    pageBrand: customPage.pageBrand,
  });

  const shareText = customPage.name;

  return (
    <ItemPageActions
      isOpen={isOpen}
      item={customPage}
      targetElRef={actionButtonRef}
      onClose={onClose}
      userCanEdit={!!customPage.userCanEdit}
      pagePath={customPage.pagePath}
      shareUrl={shareUrl}
      shareText={shareText}
      displayType="custom"
      toActionItems={useCallback(
        ({ sections }) => {
          if (customPage.userCanEdit) {
            sections[0].push({
              content: t('item.actions.viewInDashboard'),
              href: `/dashboard/custom/${customPage.id}`,
              icon: 'chart',
              testId: 'viewInDashboard',
            });

            // don't allow unpublish draft page
            if (!customPage.isDraft) {
              sections[sections.length - 1].push({
                testId: 'unpublishPage',
                content: t('item.actions.unpublishPage'),
                icon: 'unpublish',
                onClick: () => {
                  unpublishPage({
                    itemId: customPage.id,
                    itemName: customPage.name,
                    pagePath: customPage.pagePath,
                  });
                },
              });
            }

            sections[sections.length - 1].push({
              content: t('item.actions.deletePage'),
              testId: 'deletePage',
              icon: 'delete',

              onClick: async () => {
                await dispatch(deleteCustomPageAction(customPage.id));

                await showToast({
                  text: t('item.events.customPageDeleted'),
                });

                router.push('/dashboard');
              },
            });
          }

          if (artist) {
            sections.push([
              {
                content: artist.name,
                href: artist.pagePath,
                icon: 'person',
              },
            ]);
          }

          return sections;
        },
        [customPage]
      )}
    />
  );
};
